/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { DealsService, type Deal } from '@services/requests/housien-services/DealsService';

/** Shared cache key, so the header strip and the sidebar cost one request. */
export const clientDealsKey = (agencyId: string, clientId: string) =>
  ['clients', agencyId, 'deals', clientId] as const;

export interface ClientDeals {
  isLoading: boolean;
  /** Every attendance this client has, whatever its stage. */
  deals: Deal[];
}

/**
 * Every attendance recorded for one client.
 *
 * There is no endpoint for "this client's offers" or "this client's
 * responsible broker": both live inside the attendance. One listing
 * answers all of it, and every caller on the client page reads it from
 * the same cache entry rather than listing the deals again.
 *
 * @param agencyId - The owning agency.
 * @param clientId - The client to look up; empty disables the query.
 * @returns A loading flag and the client's attendances.
 */
export function useClientDeals(agencyId: string, clientId: string): ClientDeals {
  const query = useQuery({
    queryKey: clientDealsKey(agencyId, clientId),
    queryFn: () => DealsService.listDeals(agencyId, 1, 100, undefined, clientId),
    enabled: Boolean(agencyId && clientId),
  });

  return { isLoading: query.isLoading, deals: query.data?.data ?? [] };
}
